import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Badge,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add,
  SportsTennis,
  People,
  LocationOn,
  Visibility,
  Search,
  Refresh,
  Delete,
} from '@mui/icons-material';
import apiService from '../services/api';
import { Match } from '../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`matches-tabpanel-${index}`}
      aria-labelledby={`matches-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const MatchesPage: React.FC = () => {
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [openMatchDialog, setOpenMatchDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchMatches();
  }, []);

  const fetchMatches = async () => {
    try {
      setLoading(true);
      const response = await apiService.getMatches();
      console.log('Matches response:', response);
      // Handle both array and object with data property
      const data = response.data as any;
      const matchesData = Array.isArray(data) 
        ? data 
        : (data?.data || data || []);
      setMatches(matchesData);
      console.log('Set matches:', matchesData);
    } catch (error: any) {
      console.error('Error fetching matches:', error);
      console.error('Error details:', error.response?.data);
      setMatches([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMatch = () => {
    setOpenMatchDialog(true);
  };

  const handleJoinMatch = async (matchId: number) => {
    try {
      await apiService.joinMatch(matchId);
      fetchMatches(); // Refresh matches
    } catch (error) {
      console.error('Error joining match:', error);
    }
  };

  const handleLeaveMatch = async (matchId: number) => {
    try {
      await apiService.leaveMatch(matchId);
      fetchMatches(); // Refresh matches
    } catch (error: any) {
      console.error('Error leaving match:', error);
      alert(error.response?.data?.detail || error.message || 'Failed to leave match');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'open':
        return 'success';
      case 'confirmed':
        return 'warning';
      case 'completed':
        return 'info';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const filteredMatches = matches.filter(match => {
    if (!match) return false;
    
    // Filter out private matches unless user is participant/creator (backend handles this, but add safety check)
    if (match.match_type === 'PRIVATE') {
      // Backend should filter these, but add safety check
    }
    
    const matchesSearch = searchTerm === '' || 
      (match.status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
       match.court_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
       match.club_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
       String(match.id).includes(searchTerm));
    
    const matchesStatus = statusFilter === 'all' || match.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const openMatches = filteredMatches.filter(match => 
    match.status === 'open' || match.status === 'confirmed'
  );
  const myMatches = filteredMatches.filter(match => 
    match.participants_count > 0 // Simple check for now
  );
  const allMatches = filteredMatches;

  const renderMatchCard = (match: Match) => (
    <Card key={match.id} sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Match #{match.id}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Chip
                label={match.status}
                color={getStatusColor(match.status) as any}
                size="small"
              />
              {match.tournament_id && (
                <Chip
                  label="Tournament Match"
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              )}
              {match.match_type && match.match_type !== 'COMPETITIVE' && (
                <Chip
                  label={match.match_type}
                  color={match.match_type === 'FRIENDLY' ? 'success' : 'default'}
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
          </Box>
          <Box display="flex" gap={1}>
            <Tooltip title="View Details">
              <IconButton size="small">
                <Visibility />
              </IconButton>
            </Tooltip>
            {match.status === 'open' && (match.participants_count || 0) < (match.max_players || 4) && (
              <Tooltip title="Join Match">
                <IconButton 
                  size="small" 
                  color="primary"
                  onClick={() => handleJoinMatch(match.id)}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
            {match.status === 'open' && (match.participants_count || 0) > 0 && (
              <Tooltip title="Leave Match">
                <IconButton 
                  size="small" 
                  color="error"
                  onClick={() => handleLeaveMatch(match.id)}
                >
                  <Delete />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <LocationOn fontSize="small" />
          <Typography variant="body2">
            {match.court_name || `Court ID: ${match.court_id}`}
            {match.club_name && ` - ${match.club_name}`}
          </Typography>
        </Box>
        
        {match.date && (
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Typography variant="body2" color="text.secondary">
              {new Date(match.date).toLocaleDateString()}
              {match.time && ` at ${match.time}`}
            </Typography>
          </Box>
        )}

        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <People fontSize="small" />
          <Typography variant="body2">
            {match.participants_count || 0} / {match.max_players || 4} participants
          </Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Match Details:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Match ID: {match.id} | Status: {match.status} | Participants: {match.participants_count}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Matches
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Find and join padel matches in your area
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateMatch}
        >
          Create Match
        </Button>
      </Box>

      {/* Search and Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} alignItems="center">
          <Box flexGrow={1}>
            <TextField
              fullWidth
              placeholder="Search matches by court, club, or player..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Box>
          <Box minWidth={200}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchMatches}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab 
            label={
              <Badge badgeContent={openMatches.length} color="primary">
                Available Matches
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={myMatches.length} color="secondary">
                My Matches
              </Badge>
            } 
          />
          <Tab label="All Matches" />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        {openMatches.length === 0 ? (
          <Box textAlign="center" py={4}>
            <SportsTennis sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No available matches
            </Typography>
            <Typography color="text.secondary" paragraph>
              Be the first to create a match in your area!
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreateMatch}
            >
              Create Match
            </Button>
          </Box>
        ) : (
          openMatches.map(renderMatchCard)
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {myMatches.length === 0 ? (
          <Box textAlign="center" py={4}>
            <People sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No matches joined
            </Typography>
            <Typography color="text.secondary" paragraph>
              Join some matches to see them here!
            </Typography>
            <Button
              variant="contained"
              startIcon={<SportsTennis />}
              onClick={() => setTabValue(0)}
            >
              Browse Matches
            </Button>
          </Box>
        ) : (
          myMatches.map(renderMatchCard)
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {allMatches.length === 0 ? (
          <Box textAlign="center" py={4}>
            <SportsTennis sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No matches found
            </Typography>
            <Typography color="text.secondary" paragraph>
              Try adjusting your search criteria or create a new match.
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreateMatch}
            >
              Create Match
            </Button>
          </Box>
        ) : (
          allMatches.map(renderMatchCard)
        )}
      </TabPanel>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="create match"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={handleCreateMatch}
      >
        <Add />
      </Fab>

      {/* Create Match Dialog */}
      <Dialog open={openMatchDialog} onClose={() => setOpenMatchDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Match</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Match creation form would go here. This would include court selection, 
            time scheduling, participant management, and match settings.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenMatchDialog(false)}>Cancel</Button>
          <Button variant="contained">Create Match</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
